import { useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowLeft, BadgeCheck, Award, CheckCircle } from "lucide-react";
import PortalHeader from "../components/PortalHeader";
import PortalFooter from "../components/PortalFooter";

export default function Verify() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  // Strict 2 Decimal Formatter with proper rounding
  const formatDec = (val) => {
    if (val === null || val === undefined || val === "" || val === "—" || val === "-") return "—";
    const num = parseFloat(val);
    return isNaN(num) ? val : num.toFixed(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    const h = code.trim().toUpperCase();
    if (!h) return toast.error("Enter the verification code from the GS");
    
    setBusy(true);
    setData(null);
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(h)}`);
      setData(data);
    } catch (err) {
      toast.error(fmtError(err) || "Invalid Hash or Document Not Found.");
    } finally {
      setBusy(false);
    }
  };

  // Program-aware check for Final Semester
  let isFinalSem = false;
  if (data && data.result && data.student) {
    const progUpper = String(data.student.program || "").toUpperCase();
    const isShortProgram = progUpper.includes("MASTER") || progUpper.includes("M.TECH") || progUpper.includes("MCA");
    const requiredFinalSem = isShortProgram ? "IV" : "VIII";
    
    isFinalSem = data.result.semester === requiredFinalSem || (data.result.division && data.result.division !== "—");
  }

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col">
      <PortalHeader
        right={
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold hover:text-stone-800 inline-flex items-center gap-1.5"
            data-testid="verify-home-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back home
          </Link>
        }
      />

      <main className="max-w-3xl mx-auto px-6 py-14 space-y-10 flex-1 w-full">
        <Card
          data-testid="verify-search-card"
          className="bg-white border border-stone-200 p-8 md:p-10 rounded-sm shadow-sm"
        >
          <ShieldCheck className="w-9 h-9 text-emerald-700" strokeWidth={1.5} />
          <h1 className="font-display text-3xl mt-4">Verify a Grade Sheet</h1>
          <p className="text-stone-600 text-sm mt-2">
            Enter the alphanumeric verification code printed below the barcode
            on the official GBPIET Grade Sheet to confirm its authenticity.
          </p>

          <form
            onSubmit={submit}
            className="mt-6 flex flex-col sm:flex-row gap-3"
            data-testid="verify-form"
          >
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">
                Verification code
              </Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. 3B893A55D979"
                data-testid="verify-code-input"
                className="mt-1 rounded-sm font-mono uppercase tracking-wider"
                autoFocus
                required
                maxLength={32}
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="verify-submit"
              className="rounded-sm bg-emerald-700 hover:bg-emerald-600 text-white h-11 mt-1 sm:mt-6 sm:px-8"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : "Verify"}
            </Button>
          </form>
        </Card>

        {data && (
          <Card
            data-testid="verify-result-card"
            className="bg-white border border-emerald-200 p-8 md:p-10 rounded-sm shadow-sm fade-up border-t-4 border-t-emerald-600"
          >
            <div className="flex items-center gap-3">
              <BadgeCheck className="w-7 h-7 text-emerald-600" />
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-800 font-bold">
                Verified Grade Sheet
              </p>
            </div>
            <p className="font-mono text-xs text-stone-500 mt-2">
              Code: <span className="font-semibold text-stone-700">{data.gs_hash}</span>
            </p>

            <h2 className="font-display text-3xl mt-6 text-stone-900">
              {data.student?.name || "—"}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-6 font-mono text-sm bg-stone-50 p-6 rounded-sm border border-stone-100">
              <Field label="Roll No." value={data.student?.roll_no} />
              <Field label="Enrollment" value={data.student?.enroll_no} />
              <Field label="Father's Name" value={data.student?.father_name} />
              <Field label="Programme" value={data.student?.program} />
              <Field label="Branch" value={data.student?.branch} />
              <Field label="Batch" value={data.student?.batch} />
            </div>

            <div className="mt-10 border-t border-stone-200 pt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">
                Result · {data.result?.semester} Semester ·{" "}
                {data.result?.exam_session}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4 font-mono text-sm">
                <Field label="SGPA" value={formatDec(data.result?.sgpa)} />
                <Field label="CGPA" value={formatDec(data.result?.cgpa)} />
                <Field label="Earned Credits" value={data.result?.earned_credits} />
                <Field label="Result" value={data.result?.result} isResult={true} />
              </div>
              {data.result?.remark && (
                <p className="mt-4 text-xs text-red-700 bg-red-50 p-3 rounded-sm border border-red-100 font-mono font-semibold">
                  Remark: {data.result.remark}
                </p>
              )}
            </div>

            {(data.result?.subjects || []).length > 0 && (
              <div className="mt-8 overflow-x-auto border border-stone-200 rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
                      <th className="text-left p-3 font-semibold">Code</th>
                      <th className="text-left p-3 font-semibold">Subject</th>
                      <th className="text-right p-3 font-semibold">Cr.</th>
                      <th className="text-right p-3 font-semibold">Grade</th>
                      <th className="text-right p-3 font-semibold">GP</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-stone-800">
                    {data.result.subjects.map((s, i) => (
                      <tr
                        key={i}
                        className="border-t border-stone-200 hover:bg-stone-50 transition-colors"
                        data-testid={`verify-subject-row-${i}`}
                      >
                        <td className="p-3">{s.code}</td>
                        <td className="p-3 sans font-semibold text-stone-900">{s.name}</td>
                        <td className="p-3 text-right">{s.credits}</td>
                        <td className="p-3 text-right font-bold text-indigo-900">{s.grade}</td>
                        <td className="p-3 text-right">{s.grade_points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {isFinalSem && (
              <div className="bg-slate-900 text-white p-1 rounded-sm shadow-md mt-10 fade-up">
                <div className="bg-slate-800 p-6 md:p-8 rounded-sm">
                  <h2 className="text-xl md:text-2xl font-display mb-6 flex items-center gap-2 text-white">
                    <Award className="text-amber-400" /> Final Academic Award
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 font-sans">
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Final CGPA</span>
                      <span className="text-2xl font-bold text-white">{formatDec(data.result?.final_cgpa || data.result?.cgpa)}</span>
                    </div>
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Final Result</span>
                      <span className={`text-xl font-bold ${String(data.result?.final_result || data.result?.result).toUpperCase().includes('PASS') ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {(data.result?.final_result || data.result?.result) ? String(data.result.final_result || data.result.result).toUpperCase() : "—"}
                      </span>
                    </div>
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Total Credits</span>
                      <span className="text-2xl font-bold text-white">{data.result?.cuml_earned_credits || "—"}</span>
                    </div>
                    <div className="border border-slate-600 bg-slate-700/50 p-4 rounded-sm shadow-inner flex flex-col justify-center">
                      <span className="text-slate-400 text-[10px] block mb-1 font-semibold tracking-wider uppercase">Division Awarded</span>
                      <span className="text-sm font-bold text-amber-400 uppercase leading-snug">
                        {data.result?.division || "FIRST DIVISION"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-slate-400 mt-6 pt-4 border-t border-slate-600 flex items-center gap-2 font-sans">
                    <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
                    <span>Note: Conversion formula for CGPA into percentage is: Percentage = CGPA × 10</span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}
      </main>
      <PortalFooter />
    </div>
  );
}

const Field = ({ label, value, isResult }) => (
  <div>
    <p className="text-[10px] uppercase text-stone-500 font-sans font-semibold mb-1">
      {label}
    </p>
    {isResult ? (
      <p className={`font-mono font-bold text-base ${String(value).toUpperCase().includes('PASS') ? 'text-emerald-600' : 'text-amber-700'}`}>
        {value || "—"}
      </p>
    ) : (
      <p className="font-mono text-stone-900 font-bold text-base">{value || "—"}</p>
    )}
  </div>
);
